import * as React from 'react'
require('../base.module.scss')
import * as cn from 'classnames'
import * as st from './Tooltip.module.scss'

import { BaseComponentProps } from '../Common/extractClassNamesFromProps'
import { extractClassNamesFromProps } from '../Common/extractClassNamesFromProps'
import { uniqueId } from 'DVWebNodeUtils'

interface TooltipProps extends BaseComponentProps {
  content: React.ReactChild
  position?: 'top' | 'bottom' | 'left' | 'right'
  size?: 's' | 'm' | 'l'
  use?: 'info' | 'success' | 'warning' | 'error' | 'prime'
  tabIndex?: number
  id?: string
  dismissable?: boolean
  startOpen?: boolean
  onDismiss?: () => void
  dismissSeconds?: number
}

interface TooltipState {
  isVisible: boolean
  isChecked: boolean
  noJS: boolean
  position: 'top' | 'bottom' | 'left' | 'right'
  shouldPushRight: number
  shouldPushLeft: number
  debounce?: ReturnType<typeof setTimeout>
}

export class Tooltip extends React.Component<TooltipProps, TooltipState> {
  tooltipRef: React.RefObject<HTMLSpanElement>
  uniqueID: string
  timer?: number

  static defaultProps = {
    position: 'top',
    size: 's',
    tabIndex: -1,
    dismissable: false,
    startOpen: false
  }

  constructor(props: TooltipProps) {
    super(props)
    this.uniqueID = `tooltip-${uniqueId()}`
    this.tooltipRef = React.createRef();
    this.state = {
      isVisible: this.props.startOpen || false,
      isChecked: this.props.startOpen || false,
      position: this.props.position!,
      noJS: true,
      shouldPushRight: 0,
      shouldPushLeft: 0
    };
  }

  private getLeftPos() {
    if (!this.tooltipRef || !this.tooltipRef.current) {
      return 0;
    }
    const tooltipDimensions = this.tooltipRef.current.getBoundingClientRect();
    return Math.round(tooltipDimensions.left -
      parseInt(this.tooltipRef.current.style.marginLeft!));
  }
  private getRightPos() {
    if (!this.tooltipRef || !this.tooltipRef.current) {
      return 0;
    }
    const tooltipDimensions = this.tooltipRef.current.getBoundingClientRect();
    return (document.documentElement.clientWidth -
      tooltipDimensions.right +
      parseInt(this.tooltipRef.current.style.marginLeft!));
  }
  private getPosition() {
    if (['left', 'right'].includes(this.state.position) &&
      (this.getLeftPos() < 0 || this.getRightPos() < 0)
    ) {
      return 'top'
    }
    return this.state.position
  }

  updatePositions = () => {
    if (!this.state.debounce) {
      clearTimeout(this.state.debounce)
      this.setState({
        debounce: setTimeout(() => {
          const currentLeftPos = this.getLeftPos();
          const currentRightPos = this.getRightPos();
          const newPosition = this.getPosition()
          const addedMargin = parseInt(st.tooltipMargin)
          this.setState({
            shouldPushLeft: currentLeftPos < 0 ? (Math.abs(currentLeftPos) + addedMargin) : 0,
            shouldPushRight: currentRightPos < 0 ? (Math.abs(currentRightPos) + addedMargin) : 0,
            position: newPosition,
            debounce: undefined
          })
          clearTimeout(this.state.debounce!)
        }, 250)
      })
    }
  }

  startTimer = () => {
    // This prevents the timeout from starting until after the user can see the screen with the Block message.
    if (this.props.dismissSeconds && this.props.dismissSeconds > 0 && !this.timer) {
      requestAnimationFrame(() => {
        this.timer = window.setTimeout(() => {
          this.setState({
            isChecked: false,
            isVisible: false
          })
          this.timer = undefined
        }, this.props.dismissSeconds! * 1000)
      })
    }
  }

  componentDidMount() {
    this.setState({
      noJS: false,
      position: this.getPosition()
    });

    this.updatePositions();
    addEventListener('resize', this.updatePositions);

    if (!this.props.dismissable) {
      document.documentElement.addEventListener('click', this.uncheckCheckboxState);
    }
    this.startTimer()
  }
  componentDidUpdate() {
    if (this.state.isVisible) {
      this.startTimer()
    }
  }

  setVisible = () => {
    this.setState({ isVisible: true });
    this.updatePositions();
  }
  setInvisible = () => this.setState({ isVisible: false });

  dismiss = () => {
    if (this.props.onDismiss) {
      this.props.onDismiss()
    }
  }

  componentWillUnmount() {
    if (!this.props.dismissable) {
      document.documentElement.removeEventListener('click', this.uncheckCheckboxState)
    }
    removeEventListener('resize', this.updatePositions)
  }

  uncheckCheckboxState = () => {
    if (this.state.isChecked) {
      this.setState({
        isChecked: false,
        isVisible: false
      })
    }
  }

  onChange = (e: React.FormEvent<HTMLInputElement>) => {
    const checkedState = e.currentTarget.checked
    this.setState({
      isChecked: checkedState,
      isVisible: checkedState
    })
    if (checkedState) {
      this.updatePositions()
    } else {
      this.dismiss()
      window.clearTimeout(this.timer)
      this.timer = undefined
    }
  }

  render() {
    return (
      <span className={cn(
        st.container,
        st[`position-${this.state.position}`],
        st[`size-${this.props.size}`],
        {
          [st[this.props.use || '']]: Boolean(this.props.use),
          [st.dismissable]: this.props.dismissable,
          [st.noJS]: this.state.noJS
        },
        'dvui-tooltip-container'
      )}
      tabIndex={this.props.tabIndex}>
        <input className={st.hidden} type="checkbox" id={this.props.id || this.uniqueID}
          checked={this.state.isChecked}
          onChange={this.onChange}
        />
        <label
          htmlFor={this.props.id || this.uniqueID}
          className={st.subject}
          onMouseOver={!this.props.dismissable ? this.setVisible : () => {}}
          onMouseOut={!this.props.dismissable ? this.setInvisible : () => {}}
        >
          {this.props.children}
        </label>
        {(!this.props.dismissable || this.props.dismissable && this.state.isVisible) &&
          <span className={extractClassNamesFromProps(
            this.props,
            st.tooltip,
            'dvui-tooltip'
          )}
          style={{
            marginLeft: (this.state.shouldPushLeft || -this.state.shouldPushRight)
          }}
          ref={this.tooltipRef}
          >
            {this.props.dismissable &&
              <label
                className={st.closeButton}
                htmlFor={this.props.id || this.uniqueID}
                tabIndex={0}
              >
                <svg width="24" height="24">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M4.874 4.874l14.252 14.252m-14.252 0L19.126 4.874" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"/>
                  </svg>
                </svg>
              </label>
            }
            {this.props.content}
          </span>
        }
      </span>
    )
  }
}
